/**
 * Where a step's first document stands while the step has none: waiting on something above it the
 * reader has to settle (`on` names it), being drafted, or failed or late with nothing on its way.
 * The step's body and its Continue hint both read this one value, so they cannot say different
 * things about the same wait.
 */
export type FirstDraft<Upstream extends string> =
  { status: 'needs-upstream'; on: Upstream } | { status: 'drafting' } | { status: 'failed' };

/**
 * The first-draft state of a step, or null when it has none to report: its read has not landed, or
 * the document exists. A fixed study registers no Continue and asks for no draft, so nothing reads
 * the state there.
 */
export const firstDraftOf = <Upstream extends string>({
  read,
  drafted,
  waitsOn,
  failed,
}: {
  /** The step's first read has landed. */
  read: boolean;
  /** The step holds its document. */
  drafted: boolean;
  /** What above the step the draft waits on, or null when nothing does. */
  waitsOn: Upstream | null;
  /** The draft failed or ran late, and nothing is on its way. */
  failed: boolean;
}): FirstDraft<Upstream> | null => {
  if (!read || drafted) {
    return null;
  }

  if (waitsOn !== null) {
    return { status: 'needs-upstream', on: waitsOn };
  }

  return failed ? { status: 'failed' } : { status: 'drafting' };
};
